import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { UseOutletContextPayroll } from './Payroll';
import AbsenceModal, { absenceReasonLabel } from './AbsenceModal';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import {
  MonthlyDeclaration,
  EligibleEmployee,
  EligibleEmployeesResponse,
  Absence,
  SalaryElementType,
  PunctualRubrique,
} from '../../../models/monthly-declaration.model';
import { PayrollRun } from '../../../models/payroll-run.model';
import { DeclarationDA } from '../../../services/admin/declaration.service';
import { PayrollRunDA } from '../../../services/admin/payroll-run.service';
import AppPreloader from '../../../components/Preloader';

const MONTH_KEYS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
] as const;

function monthName(lang: any, month: number): string {
  return lang.labels[MONTH_KEYS[month - 1]] ?? String(month);
}

function lastDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

const DeclarationPage: React.FunctionComponent = () => {
  const { id_run, id_employee } = useParams<{ id_run: string; id_employee: string }>();
  const { setRoute, companyForUser } = UseOutletContextPayroll();
  const { lang } = StateLanguage();

  useEffect(() => {
    setRoute({ path: { root: lang.labels.payroll, branch: lang.labels.declaration || 'Declaration' }, company: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [run, setRun] = useState<PayrollRun | null>(null);
  const [employee, setEmployee] = useState<EligibleEmployee | null>(null);
  const [declaration, setDeclaration] = useState<MonthlyDeclaration | null>(null);
  const [absences, setAbsences] = useState<Absence[]>([]);
  const [salaryElementTypes, setSalaryElementTypes] = useState<SalaryElementType[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);

  // Editable state
  const [hours, setHours] = useState(0);
  const [rubriques, setRubriques] = useState<PunctualRubrique[]>([]);
  const [dirty, setDirty] = useState(false);

  // Absence modal state
  const [editingAbsence, setEditingAbsence] = useState<Absence | null>(null);
  const [absenceModalOpen, setAbsenceModalOpen] = useState(false);

  const companyId = companyForUser?.id ?? '';

  const editable = useMemo(() => {
    if (!run) return false;
    return run.status === 'draft' || run.status === 'calculated';
  }, [run]);

  const isHourly = employee?.contract?.salary_mode === 'hourly';

  const periodFrom = useMemo(() => {
    if (!run) return '';
    return `${run.period.year}-${String(run.period.month).padStart(2, '0')}-01`;
  }, [run]);

  const periodTo = useMemo(() => {
    if (!run) return '';
    const y = run.period.year;
    const m = run.period.month;
    return `${y}-${String(m).padStart(2, '0')}-${lastDayOfMonth(y, m)}`;
  }, [run]);

  const loadData = useCallback(async () => {
    if (!companyId || !id_run || !id_employee) return;
    setLoading(true);
    setLoadError(false);
    try {
      // Load run info
      const runRes = await PayrollRunDA.getPayrollRun(companyId, id_run);
      if (runRes.status !== 200) throw new Error('Run not found');
      const runData = runRes.data as PayrollRun;
      setRun(runData);

      const year = runData.period.year;
      const month = runData.period.month;
      const pFrom = `${year}-${String(month).padStart(2, '0')}-01`;
      const pTo = `${year}-${String(month).padStart(2, '0')}-${lastDayOfMonth(year, month)}`;

      // Load in parallel: eligible employees, declarations, absences, salary element types
      const [eligibleRes, declRes, absRes, setRes] = await Promise.all([
        DeclarationDA.getEligibleEmployees(companyId, id_run),
        DeclarationDA.getDeclarations(companyId, year, month),
        DeclarationDA.getAbsences(companyId, id_employee, { from: pFrom, to: pTo }),
        DeclarationDA.getSalaryElementTypes(companyId),
      ]);

      // Find this employee in eligible list
      const eligibleData: EligibleEmployeesResponse = eligibleRes.status === 200
        ? eligibleRes.data
        : { eligible: [], excluded: [] };
      const emp = (eligibleData.eligible ?? []).find(e => e.id === id_employee) ?? null;
      setEmployee(emp);

      // Find declaration for this employee
      const declarations: MonthlyDeclaration[] = declRes.status === 200
        ? (Array.isArray(declRes.data) ? declRes.data : declRes.data?.items ?? [])
        : [];
      const decl = declarations.find(d => d.id_employee === id_employee) ?? null;
      setDeclaration(decl);

      // Absences
      const absData = absRes.status === 200
        ? (Array.isArray(absRes.data) ? absRes.data : absRes.data?.items ?? [])
        : [];
      setAbsences(absData);

      // Salary element types
      const setData = setRes.status === 200
        ? (Array.isArray(setRes.data) ? setRes.data : setRes.data?.items ?? [])
        : [];
      setSalaryElementTypes(setData);

      // Initialize editable fields from existing declaration
      setHours(decl?.quantities?.find(q => q.code === 'BASE_SALARY_HOURLY')?.quantity ?? 0);
      setRubriques(decl?.quantities
        ?.filter(q => q.code !== 'BASE_SALARY_HOURLY')
        ?.map(q => ({ code: q.code, amount: q.quantity, quantity: null, rate: null }))
        ?? []
      );
      setDirty(false);
    } catch (err) {
      console.error('[DeclarationPage] Failed to load data:', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [companyId, id_run, id_employee]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = async () => {
    if (!companyId || !id_employee || !run) return;
    setSaving(true);

    const quantities: { code: string; quantity: number }[] = [];
    if (isHourly && hours > 0) {
      quantities.push({ code: 'BASE_SALARY_HOURLY', quantity: hours });
    }

    const punctual_rubriques = rubriques.length > 0
      ? rubriques.map(r => ({
          code: r.code,
          amount: r.amount ?? null,
          quantity: r.quantity ?? null,
          rate: r.rate ?? null,
        }))
      : undefined;

    const body = {
      year: run.period.year,
      month: run.period.month,
      quantities: quantities.length > 0 ? quantities : undefined,
      punctual_rubriques,
    };

    const response = await DeclarationDA.upsertDeclaration(companyId, id_employee, body);
    if (response.status === 200 || response.status === 201) {
      // Refresh declaration
      const refreshRes = await DeclarationDA.refreshDeclaration(companyId, id_employee, {
        year: run.period.year,
        month: run.period.month,
      });
      const updatedDecl = (refreshRes.status === 200 ? refreshRes.data : response.data) as MonthlyDeclaration;
      setDeclaration(updatedDecl);
      setDirty(false);
      toast.success(lang.labels.actionSuccessful || 'Success');
    } else {
      toast.error(response.data?.message || lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain);
    }
    setSaving(false);
  };

  const handleOpenAbsenceModal = (absence?: Absence | null) => {
    setEditingAbsence(absence ?? null);
    setAbsenceModalOpen(true);
  };

  const handleAbsenceSuccess = async () => {
    if (!companyId || !id_employee || !run) return;
    setAbsenceModalOpen(false);
    // Reload absences and refresh declaration
    const [absRes, refreshRes] = await Promise.all([
      DeclarationDA.getAbsences(companyId, id_employee, { from: periodFrom, to: periodTo }),
      DeclarationDA.refreshDeclaration(companyId, id_employee, {
        year: run.period.year,
        month: run.period.month,
      }),
    ]);
    if (absRes.status === 200) {
      setAbsences(Array.isArray(absRes.data) ? absRes.data : absRes.data?.items ?? []);
    }
    if (refreshRes.status === 200) {
      setDeclaration(refreshRes.data);
    }
  };

  // Rubrique helpers
  const addRubrique = () => {
    if (salaryElementTypes.length === 0) return;
    const usedCodes = new Set(rubriques.map(r => r.code));
    const available = salaryElementTypes.filter(t => !usedCodes.has(t.code) && t.calculation !== 'computed');
    if (available.length === 0) return;
    setRubriques([...rubriques, { code: available[0].code, amount: 0, quantity: null, rate: null }]);
    setDirty(true);
  };

  const updateRubrique = (index: number, update: Partial<PunctualRubrique>) => {
    setRubriques(prev => prev.map((r, i) => i === index ? { ...r, ...update } : r));
    setDirty(true);
  };

  const removeRubrique = (index: number) => {
    setRubriques(prev => prev.filter((_, i) => i !== index));
    setDirty(true);
  };

  const elementTypeName = (code: string): string => {
    const t = salaryElementTypes.find(s => s.code === code);
    if (!t) return code;
    return t.name[lang.code] ?? t.name.fr ?? t.name.en ?? code;
  };

  // Loading
  if (loading) return <AppPreloader />;

  // Error
  if (loadError) {
    return (
      <div className="card mb-5">
        <div className="card-body text-center py-10">
          <i className="bi bi-exclamation-triangle-fill text-danger fs-1 mb-3 d-block"></i>
          <p className="text-muted fw-bold fs-6 mb-3">{lang.labels.loadError}</p>
          <button className="btn btn-sm btn-primary" onClick={loadData}>
            <i className="bi bi-arrow-clockwise me-1"></i>
            {lang.labels.retry}
          </button>
        </div>
      </div>
    );
  }

  if (!run || !employee) {
    return (
      <div className="card mb-5">
        <div className="card-body text-center py-10">
          <p className="text-muted fw-bold">{lang.labels.noEligibleEmployees}</p>
          <Link to={`/${lang.code}/main/payroll/runs/view/${id_run}`} className="btn btn-sm btn-light mt-3">
            <i className="bi bi-arrow-left me-1"></i>{lang.labels.backToRun}
          </Link>
        </div>
      </div>
    );
  }

  const availableRubriqueTypes = salaryElementTypes.filter(
    t => t.calculation !== 'computed' && !rubriques.some(r => r.code === t.code)
  );

  return (
    <div className="w-100 h-100">
      {/* Back link */}
      <div className="mb-5">
        <Link to={`/${lang.code}/main/payroll/runs/view/${id_run}`} className="btn btn-sm btn-light">
          <i className="bi bi-arrow-left me-1"></i>
          {lang.labels.backToPayrollRun || 'Back to payroll run'}
        </Link>
      </div>

      {/* Header card */}
      <div className="card mb-5">
        <div className="card-body">
          <h3 className="fw-bolder mb-1">
            {(lang.labels.declarationFor || 'Declaration for {name}').replace('{name}', employee.name || employee.employee_number)}
          </h3>
          <span className="text-muted fs-7">
            {monthName(lang, run.period.month)} {run.period.year}
            <span className="mx-2">|</span>
            <span className={`badge ${isHourly ? 'badge-light-info' : 'badge-light-primary'} fs-8`}>
              {isHourly ? lang.labels.salaryMode_hourly : lang.labels.salaryMode_monthly}
            </span>
            {employee.contract?.activity_rate != null && (
              <span className="ms-2 text-muted">
                {lang.labels.activityRate}: {employee.contract.activity_rate}%
              </span>
            )}
          </span>
        </div>
      </div>

      {/* Summary card — read-only computed fields */}
      <div className="card mb-5">
        <div className="card-body">
          <div className="row text-center">
            <div className="col">
              <span className="text-muted fw-bold d-block fs-7">{lang.labels.socialInsuranceDays}</span>
              <span className="fw-bolder fs-5">{declaration?.as_days ?? employee.periodDays?.full ?? 30}</span>
            </div>
            <div className="col">
              <span className="text-muted fw-bold d-block fs-7">{lang.labels.absenceDays}</span>
              <span className="fw-bolder fs-5">{declaration?.absence_days ?? 0}</span>
            </div>
            <div className="col">
              <span className="text-muted fw-bold d-block fs-7">{lang.labels.absences}</span>
              <span className="fw-bolder fs-5">{absences.length}</span>
            </div>
            {declaration?.status && (
              <div className="col">
                <span className="text-muted fw-bold d-block fs-7">{lang.labels.status}</span>
                <span className={`badge ${declaration.status === 'validated' ? 'badge-light-success' : 'badge-light-warning'} fs-8`}>
                  {declaration.status === 'validated' ? lang.labels.declarationValidated : lang.labels.declarationDraft}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hours section — only for hourly employees */}
      {isHourly && (
        <div className="card mb-5">
          <div className="card-header">
            <div className="card-title">
              <h4 className="fw-bolder">{lang.labels.workingHours}</h4>
            </div>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-4">
                <label className="form-label fw-bold">{lang.labels.workingHours}</label>
                {editable ? (
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    className="form-control form-control-sm"
                    value={hours}
                    onChange={e => { setHours(parseFloat(e.target.value) || 0); setDirty(true); }}
                  />
                ) : (
                  <span className="d-block fw-bold">{hours}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Absences section */}
      <div className="card mb-5">
        <div className="card-header">
          <div className="card-title">
            <h4 className="fw-bolder">{lang.labels.absences}</h4>
          </div>
          {editable && (
            <div className="card-toolbar">
              <button className="btn btn-sm btn-light-primary" onClick={() => handleOpenAbsenceModal()}>
                <i className="bi bi-plus-lg me-1"></i>{lang.labels.addAbsence}
              </button>
            </div>
          )}
        </div>
        <div className="card-body">
          {absences.length === 0 ? (
            <p className="text-muted fw-bold text-center py-3">{lang.labels.noAbsences}</p>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle table-row-dashed mb-0">
                <thead className="border-bottom">
                  <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                    <th>{lang.labels.absenceReason}</th>
                    <th>{lang.labels.absenceFrom}</th>
                    <th>{lang.labels.absenceTo}</th>
                    <th>{lang.labels.absenceDays}</th>
                    <th>{lang.labels.incapacityRate}</th>
                    <th>{lang.labels.salaryContinuationRate}</th>
                    {editable && <th className="w-50px"></th>}
                  </tr>
                </thead>
                <tbody className="text-secondary fw-bold">
                  {absences.map(a => {
                    const days = a.to ? (a.from ? Math.max(0, Math.round((new Date(a.to).getTime() - new Date(a.from).getTime()) / 86400000) + 1) : 0) : null;
                    return (
                      <tr key={a.id}>
                        <td><span className="badge badge-light-danger">{absenceReasonLabel(lang, a.reason)}</span></td>
                        <td>{a.from?.substring(0, 10)}</td>
                        <td>{a.to?.substring(0, 10) || <span className="badge badge-light-info">{lang.labels.absenceOngoing}</span>}</td>
                        <td>{days ?? '-'}</td>
                        <td>{a.incapacity_rate}%</td>
                        <td>{a.salary_continuation_rate}%</td>
                        {editable && (
                          <td>
                            <button
                              className="btn btn-sm btn-icon btn-light-warning"
                              title={lang.labels.editAbsence}
                              onClick={() => handleOpenAbsenceModal(a)}
                            >
                              <i className="bi bi-pencil fs-8"></i>
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Punctual rubriques section */}
      <div className="card mb-5">
        <div className="card-header">
          <div className="card-title">
            <h4 className="fw-bolder">{lang.labels.punctualRubriques}</h4>
          </div>
          {editable && availableRubriqueTypes.length > 0 && (
            <div className="card-toolbar">
              <button className="btn btn-sm btn-light-primary" onClick={addRubrique}>
                <i className="bi bi-plus-lg me-1"></i>{lang.labels.addRubrique}
              </button>
            </div>
          )}
        </div>
        <div className="card-body">
          {rubriques.length === 0 ? (
            <p className="text-muted fw-bold text-center py-3">{lang.labels.noRubriques}</p>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle table-row-dashed mb-0">
                <thead className="border-bottom">
                  <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                    <th className="min-w-200px">{lang.labels.rubriqueCode}</th>
                    <th className="min-w-150px">{lang.labels.rubriqueLabel}</th>
                    <th className="min-w-100px">{lang.labels.rubriqueAmount}</th>
                    {editable && <th className="w-50px"></th>}
                  </tr>
                </thead>
                <tbody className="text-secondary fw-bold">
                  {rubriques.map((rub, idx) => {
                    const typeInfo = salaryElementTypes.find(t => t.code === rub.code);
                    const calcMode = typeInfo?.calculation ?? 'fixed_amount';
                    return (
                      <tr key={idx}>
                        <td>
                          {editable ? (
                            <select
                              className="form-select form-select-sm"
                              value={rub.code}
                              onChange={e => updateRubrique(idx, { code: e.target.value })}
                            >
                              <option value={rub.code}>{elementTypeName(rub.code)}</option>
                              {availableRubriqueTypes.map(t => (
                                <option key={t.code} value={t.code}>{elementTypeName(t.code)}</option>
                              ))}
                            </select>
                          ) : (
                            <span>{rub.code}</span>
                          )}
                        </td>
                        <td>{elementTypeName(rub.code)}</td>
                        <td>
                          {editable ? (
                            calcMode === 'quantity_rate' ? (
                              <div className="d-flex gap-2">
                                <input
                                  type="number"
                                  min={0}
                                  step={0.01}
                                  className="form-control form-control-sm"
                                  placeholder={lang.labels.rubriqueQuantity}
                                  value={rub.quantity ?? ''}
                                  onChange={e => updateRubrique(idx, { quantity: parseFloat(e.target.value) || 0 })}
                                />
                                <input
                                  type="number"
                                  min={0}
                                  step={0.01}
                                  className="form-control form-control-sm"
                                  placeholder={lang.labels.rubriqueRate}
                                  value={rub.rate ?? ''}
                                  onChange={e => updateRubrique(idx, { rate: parseFloat(e.target.value) || 0 })}
                                />
                              </div>
                            ) : (
                              <input
                                type="number"
                                min={0}
                                step={0.01}
                                className="form-control form-control-sm"
                                value={rub.amount ?? ''}
                                onChange={e => updateRubrique(idx, { amount: parseFloat(e.target.value) || 0 })}
                              />
                            )
                          ) : (
                            <span>{calcMode === 'quantity_rate' ? `${rub.quantity ?? 0} x ${rub.rate ?? 0}` : rub.amount ?? 0}</span>
                          )}
                        </td>
                        {editable && (
                          <td>
                            <button
                              className="btn btn-sm btn-icon btn-light-danger"
                              title={lang.labels.removeRubrique}
                              onClick={() => removeRubrique(idx)}
                            >
                              <i className="bi bi-trash fs-8"></i>
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Save button */}
      {editable && (
        <div className="d-flex justify-content-end mb-5 gap-2">
          <Link to={`/${lang.code}/main/payroll/runs/view/${id_run}`} className="btn btn-sm btn-light">
            {lang.labels.cancel}
          </Link>
          <button
            className="btn btn-sm btn-primary"
            onClick={handleSave}
            disabled={saving || !dirty}
          >
            {saving
              ? <span className="spinner-border spinner-border-sm me-1"></span>
              : <i className="bi bi-check-lg me-1"></i>
            }
            {lang.labels.saveDeclaration}
          </button>
        </div>
      )}

      {/* Absence modal */}
      {absenceModalOpen && (
        <AbsenceModal
          companyId={companyId}
          employeeId={id_employee!}
          lang={lang}
          absence={editingAbsence}
          onSuccess={handleAbsenceSuccess}
          onClose={() => setAbsenceModalOpen(false)}
        />
      )}
    </div>
  );
};

export default DeclarationPage;
